import { domainError, type DomainError } from '../shared/domain-error';
import type { ProjectId, StructuralElementTypeId } from '../shared/ids';
import { normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import { checkTypeLines, type ItemFactor } from './own-factor';
import type { SteelRate } from './steel';
import { STRUCTURAL_ELEMENT_KIND_LABEL, type StructuralElementKind } from './structural-element';

/**
 * O tipo de pilar e o tipo de viga (Fase 42, etapa 1 — G1, G5, G7; RN-561, RN-562; ADR-188).
 *
 * "P20", "V15×50": o pilar de canto leva outro concreto por m³ que o do meio, e escrever o fator
 * pilar a pilar seria repetir o mesmo número em cada card de cada andar. O tipo guarda a
 * **composição** — o fator de cada item dos serviços que medem o elemento —, e cada pilar ou viga
 * aponta o tipo dele. É da obra, como o tipo de bloco (Fase 41), e não tem medidas (G7): a seção e
 * a corrida seguem em cada peça do desenho.
 *
 * O pilar e a viga nunca se misturam (RN-257): cada tipo é de um elemento, e o nome é único na
 * obra **dentro** do elemento — "P1" pode ser tipo de pilar e de viga.
 *
 * A ordem do fator de um item numa peça: o próprio do card; sem ele, o do tipo; sem ele, o índice
 * da obra. O item que o tipo não tem usa o índice, como no bloco (RN-280).
 */
export interface StructuralElementType {
  readonly id: StructuralElementTypeId;
  readonly projectId: ProjectId;
  /** De que elemento é o tipo: o pilar aponta tipo de pilar, a viga, tipo de viga. */
  readonly kind: StructuralElementKind;
  /** "P20" — único entre os tipos vivos do mesmo elemento na obra. */
  readonly name: string;
  /** A composição: o fator de cada item, pela chave do serviço e a do item. Vazia: segue a obra. */
  readonly rates: readonly ItemFactor[];
  /**
   * A taxa de aço do tipo, kg/m³ por bitola (Fase 42, etapa 2 — G6; RN-567). Vazia ou ausente: a
   * peça do tipo usa a da disciplina. A lista vale inteira, como a do tipo de bloco (RN-543): a
   * peça com taxa própria usa a dela, e nenhuma lista se mistura com a de baixo.
   */
  readonly steelRates?: readonly SteelRate[];
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export const ELEMENT_TYPE_ERROR = {
  NAME_REQUIRED: 'TAKEOFF.ELEMENT_TYPE_NAME_REQUIRED',
  NAME_TAKEN: 'TAKEOFF.ELEMENT_TYPE_NAME_TAKEN',
  NAME_TOO_LONG: 'TAKEOFF.ELEMENT_TYPE_NAME_TOO_LONG',
  NOT_FOUND: 'TAKEOFF.ELEMENT_TYPE_NOT_FOUND',
  /** O pilar que aponta tipo de viga, ou o contrário (RN-257). */
  WRONG_KIND: 'TAKEOFF.ELEMENT_TYPE_WRONG_KIND',
  /** O tipo que sai da obra com pilar ou viga do desenho ainda apontando para ele. */
  IN_USE: 'TAKEOFF.ELEMENT_TYPE_IN_USE',
} as const;

/** "P20-CANTO-GARAGEM" cabe; um nome de parágrafo não. Como o do tipo de bloco. */
export const ELEMENT_TYPE_NAME_MAX = 40;

/** "tipo de pilar", "tipo de viga" — como a mensagem fala do tipo. */
const doTipo = (kind: StructuralElementKind): string =>
  `tipo de ${STRUCTURAL_ELEMENT_KIND_LABEL[kind].toLocaleLowerCase('pt-BR')}`;

/**
 * O tipo conferido: o nome obrigatório, curto e único entre os tipos do mesmo elemento na obra, e
 * a composição com um fator positivo por item, com a origem e a foto de cada linha (Fase 42, etapa
 * 4 — `checkTypeLines`, ADR-191).
 */
export function checkElementTypeFields(
  fields: {
    readonly kind: StructuralElementKind;
    readonly name: string;
    readonly rates: readonly ItemFactor[];
  },
  others: readonly Pick<StructuralElementType, 'id' | 'kind' | 'name' | 'deletedAt'>[],
  selfId?: StructuralElementTypeId,
): Result<{ readonly name: string; readonly rates: readonly ItemFactor[] }, DomainError> {
  const nome = fields.name.trim();
  const rotulo = doTipo(fields.kind);
  if (nome === '') {
    return err(domainError(ELEMENT_TYPE_ERROR.NAME_REQUIRED, `O nome do ${rotulo} é obrigatório.`));
  }
  if (nome.length > ELEMENT_TYPE_NAME_MAX) {
    return err(
      domainError(
        ELEMENT_TYPE_ERROR.NAME_TOO_LONG,
        `O nome do ${rotulo} tem no máximo ${String(ELEMENT_TYPE_NAME_MAX)} caracteres.`,
      ),
    );
  }
  const chave = normalizeName(nome);
  if (
    others.some(
      (outro) =>
        outro.deletedAt === undefined &&
        outro.id !== selfId &&
        outro.kind === fields.kind &&
        normalizeName(outro.name) === chave,
    )
  ) {
    return err(
      domainError(ELEMENT_TYPE_ERROR.NAME_TAKEN, `Já existe o ${rotulo} ${nome} nesta obra.`, {
        name: nome,
      }),
    );
  }
  const composicao = checkTypeLines(fields.rates);
  if (!composicao.ok) return composicao;
  return ok({ name: nome, rates: composicao.value });
}

/**
 * O tipo que a peça pode apontar: vivo e do mesmo elemento (RN-257). O pilar com tipo de viga é
 * recusado, e não ignorado — é um envelope que se contradiz.
 */
export function checkElementTypeOf(
  element: { readonly kind: StructuralElementKind; readonly elementTypeId?: string },
  types: ReadonlyMap<string, Pick<StructuralElementType, 'kind' | 'name' | 'deletedAt'>>,
): Result<void, DomainError> {
  if (element.elementTypeId === undefined) return ok(undefined);
  const tipo = types.get(element.elementTypeId);
  if (tipo === undefined || tipo.deletedAt !== undefined) {
    return err(
      domainError(ELEMENT_TYPE_ERROR.NOT_FOUND, 'O tipo da peça não existe nesta obra.', {
        elementTypeId: element.elementTypeId,
      }),
    );
  }
  if (tipo.kind !== element.kind) {
    return err(
      domainError(
        ELEMENT_TYPE_ERROR.WRONG_KIND,
        `${tipo.name} é ${doTipo(tipo.kind)}, e não ${doTipo(element.kind)}.`,
        { elementTypeId: element.elementTypeId },
      ),
    );
  }
  return ok(undefined);
}
